import { NetworkService, SyncService } from "../Stats/GameLogger/GameLoggerService";

// Replace these with your actual API endpoints
const API_ENDPOINTS = {
  SYNC_GAMES: '/api/games/sync',
  SYNC_PLAYS: '/api/plays/sync'
};

export class SyncManager {
  private syncInProgress = false;
  private syncInterval: NodeJS.Timeout | null = null;
  private syncIntervalMs = 30000; // Try to sync every 30 seconds
  
  constructor() {
    // Set up network status listeners
    NetworkService.addOnlineListener(this.handleOnline);
    NetworkService.addOfflineListener(this.handleOffline);
  }
  
  public startSync(): void {
    if (this.syncInterval) return;
    
    // Attempt immediate sync
    this.attemptSync();
    
    // Set up interval for future sync attempts
    this.syncInterval = setInterval(() => {
      this.attemptSync();
    }, this.syncIntervalMs);
  }
  
  public stopSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }
  
  public async forceSyncNow(): Promise<boolean> {
    return await this.attemptSync();
  }
  
  private handleOnline = (): void => {
    console.log('Network connection restored. Starting sync...');
    this.startSync();
  }
  
  private handleOffline = (): void => {
    console.log('Network connection lost. Pausing sync...');
    this.stopSync();
  }
  
  private async attemptSync(): Promise<boolean> {
    // Don't attempt if already syncing or offline
    if (this.syncInProgress || !NetworkService.isOnline()) {
      return false;
    }
    
    try {
      this.syncInProgress = true;
      
      // Get all unsynced data
      const games = await SyncService.getUnsyncedGames();
      const plays = await SyncService.getUnsyncedPlays();
      
      if (games.length === 0 && plays.length === 0) {
        return true; // Nothing to sync
      }
      
      // Sync games
      if (games.length > 0) {
        const response = await fetch(API_ENDPOINTS.SYNC_GAMES, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ games })
        });
        
        if (response.ok) {
          const result = await response.json();
          // Mark games as synced
          for (const game of result.syncedGames) {
            await SyncService.markGameSynced(game.id);
          }
        } else {
          throw new Error('Failed to sync games');
        }
      }
      
      // Sync plays
      if (plays.length > 0) {
        const response = await fetch(API_ENDPOINTS.SYNC_PLAYS, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ plays })
        });
        
        if (response.ok) {
          const result = await response.json();
          // Mark plays as synced
          for (const play of result.syncedPlays) {
            await SyncService.markPlaySynced(play.id);
          }
        } else {
          throw new Error('Failed to sync plays');
        }
      }
      
      console.log('Sync completed successfully');
      return true;
    } catch (error) {
      console.error('Sync failed:', error);
      return false;
    } finally {
      this.syncInProgress = false;
    }
  }
  
  public cleanup(): void {
    this.stopSync();
    NetworkService.removeOnlineListener(this.handleOnline);
    NetworkService.removeOfflineListener(this.handleOffline);
  }
}

// Create and export a singleton instance
export const syncManager = new SyncManager();